/**
 * Annual Review — compliance's campaign console.
 * Launch a review over the active policy set (the launch itself emails every
 * owner, citing the compliance-program element that requires it), watch
 * progress, and act as the checker: open each submission, edit it if needed,
 * approve to cut the new version (revision date at the top, revision row at
 * the bottom) and reset the review clock, or return it to the owner with a
 * note. Every email sent and every reply is kept under Correspondence,
 * organized by department.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ConditionAnswer, { fetchConditionAnswers } from './ConditionAnswer';
import PolicyParagraphEditor, { editsSummary, toRequestEdits, DiffText } from './PolicyParagraphEditor';
import ReviewCorrespondence from './ReviewCorrespondence';
import { TakeOnBlock, RuleHomesList, usePolicyChoices } from './RuleTakeOn';
import { anchorFindings } from './reviewAnchors';
import { DiscussPanel, AskGuidePanel } from '../Workspace';

const API = process.env.REACT_APP_API_URL;

/**
 * The final rule against the policy set, for compliance: every requirement
 * the organization owes that no policy covers, with the recommended home or
 * "not sure", and the controls to decide. Sits above the campaign list.
 */
function RuleHomesPanel() {
  const [state, setState] = useState({ loading: true, homes: [], analysis: null });
  const [open, setOpen] = useState(true);
  const policies = usePolicyChoices(true);
  const load = useCallback(() => {
    fetch(`${API}/api/ppm/rule-homes`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => setState({ loading: false, homes: d.success ? (d.homes || []) : [], analysis: d.success ? d.analysis : null }))
      .catch(() => setState({ loading: false, homes: [], analysis: null }));
  }, []);
  useEffect(() => { load(); }, [load]);
  if (state.loading) return null;
  if (!state.analysis) return null;
  const homes = state.homes;
  const openItems = homes.filter(h => h.status !== 'covered');
  const covered = homes.filter(h => h.status === 'covered' && !h.decided);
  return (
    <div className="mt-5 rounded-xl border border-orange-200 bg-white p-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-sm font-semibold text-slate-800">{state.analysis.title || 'Final rule'}: what no policy covers yet</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            {homes.length} requirement{homes.length === 1 ? '' : 's'} the organization owes under this rule. {covered.length} covered by a policy that cites the section or governs the subject. {openItems.length} with no policy, listed below with the recommended home, or "not sure" where the reader could not tell. Assign each one; it then appears on that policy's review.
          </p>
        </div>
        <button type="button" onClick={() => setOpen(v => !v)} className="text-xs text-slate-500 hover:text-slate-700">{open ? 'Hide' : 'Show'}</button>
      </div>
      {open && (
        <div className="mt-3">
          <RuleHomesList homes={homes} canDecide policies={policies} compact
            decideUrl={(h) => `${API}/api/ppm/rule-homes/${h.id}/decide`} onChanged={load}
            emptyText="Every requirement of this rule is covered by a policy." />
        </div>
      )}
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(String(iso).slice(0, 10) + 'T12:00:00Z').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return '—'; }
}

const STATUS_CHIP = {
  pending:   'bg-slate-100 text-slate-600 border-slate-200',
  submitted: 'bg-sky-50 text-sky-700 border-sky-200',
  approved:  'bg-emerald-50 text-emerald-700 border-emerald-200',
  returned:  'bg-amber-50 text-amber-700 border-amber-200',
};
const STATUS_LABEL = { pending: 'Waiting on owner', submitted: 'Ready for your review', approved: 'Approved', returned: 'Returned to owner' };

/** Stored before/after pairs → editor ops. */
function pairsToOps(pairs) {
  return (Array.isArray(pairs) ? pairs : []).map((p, i) => (
    p.op === 'delete' ? { index: p.index, op: 'delete' }
      : p.op === 'insert_after' ? { index: p.index, op: 'insert_after', text: p.after || '', key: `stored-${i}` }
        : { index: p.index, op: 'replace', text: p.after || '' }
  ));
}

/**
 * What the owner saw: the Policy Review Guide's section-level check, expanded
 * inline under a submitted row. Read-only — the checker still decides with
 * Approve / Return.
 */
function GuidanceDetail({ taskId, canRun = false, onChanged = null, onData = null, compact = false }) {
  const [state, setState] = useState({ loading: true });
  const [condAnswers, setCondAnswers] = useState(null);
  const [starting, setStarting] = useState(false);
  // Callbacks live in refs: a parent re-render must never restart the read.
  // (Inline arrows as effect deps looped read → parent reload → new arrow → read.)
  const onDataRef = useRef(onData); const onChangedRef = useRef(onChanged);
  useEffect(() => { onDataRef.current = onData; onChangedRef.current = onChanged; });
  useEffect(() => { fetchConditionAnswers().then(setCondAnswers); }, []);
  // One read of the current version's guidance. While a run is in progress
  // the poll continues every few seconds until it lands (ready or failed).
  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/ppm-review/tasks/${taskId}/guidance`, { credentials: 'include' });
      const d = await r.json().catch(() => ({}));
      if (r.status === 202) return { generating: true };
      if (d?.status === 'ready') return { data: d };
      if (r.status === 404) return { none: true };
      return { message: d?.error_message || d?.message || 'The review is unavailable for this task.' };
    } catch { return { message: 'The review is unavailable right now.' }; }
  }, [taskId]);
  useEffect(() => {
    let alive = true; let timer = null;
    const tick = async () => {
      const s = await load();
      if (!alive) return;
      setState(s);
      if (s.generating) timer = setTimeout(tick, 4000);
      else if (s.data) { onDataRef.current?.(s.data); }
    };
    tick();
    return () => { alive = false; clearTimeout(timer); };
  }, [load]);
  const start = async (again = false) => {
    setStarting(true);
    try {
      const r = await fetch(`${API}/api/ppm-review/tasks/${taskId}/guidance/generate`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(again ? { again: true } : {}) });
      const d = await r.json().catch(() => ({}));
      if (r.ok || r.status === 202) {
        setState({ generating: true });
        // Poll from here; the mount effect only polls when it found a run in flight.
        const poll = async () => {
          const s = await load();
          setState(s);
          if (s.generating) setTimeout(poll, 4000);
          else if (s.data) { onDataRef.current?.(s.data); onChangedRef.current?.(); }
        };
        setTimeout(poll, 3000);
      } else setState({ none: true, message: d?.message || 'The review could not start.' });
    } catch { setState({ none: true, message: 'The review could not start.' }); }
    setStarting(false);
  };

  useEffect(() => {
    const onRerun = (e) => { if (e?.detail?.taskId === taskId && canRun) start(true); };
    window.addEventListener('ppm:rerun-review', onRerun);
    return () => window.removeEventListener('ppm:rerun-review', onRerun);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId, canRun]);
  const runAgain = canRun ? (
    <button type="button" disabled={starting} onClick={() => start(true)} title="Read this version again, applying every lesson taught since"
      className="ml-2 text-[11.5px] font-semibold text-slate-600 hover:text-slate-900 underline-offset-2 hover:underline disabled:opacity-50">Run again</button>
  ) : null;
  if (state.loading) return <p className="text-xs text-slate-400 py-2">Loading the review…</p>;
  if (state.generating) return (
    <p className="text-xs text-slate-600 py-2 flex items-center gap-2">
      <span className="inline-block w-3 h-3 rounded-full border-2 border-slate-300 border-t-slate-700 animate-spin" aria-hidden />
      Checking this policy against the final rule, the memos and the state contract. This takes a minute or two; the result appears here.
    </p>
  );
  if (state.none) return (
    <div className="py-2 flex items-center gap-3 flex-wrap">
      <p className="text-xs text-slate-500">{canRun ? 'The review has not been run on this version.' : 'The owner did not open guidance for this version.'}{state.message ? ` ${state.message}` : ''}</p>
      {canRun && (
        <button type="button" disabled={starting} onClick={() => start(false)}
          className="px-3 py-1.5 rounded-lg bg-slate-800 text-white text-xs font-semibold hover:bg-slate-900 disabled:opacity-50">
          {starting ? 'Starting…' : 'Run the review'}
        </button>
      )}
    </div>
  );
  if (state.message) return <p className="text-xs text-slate-500 py-2">{state.message}</p>;
  const g = state.data.guidance || {};
  const sections = Array.isArray(g.sections) ? g.sections : [];
  const flags = sections.filter(s => s.status === 'flag');
  const unverified = sections.filter(s => s.status === 'unverified');
  const clean = sections.filter(s => s.status === 'no_changes');
  const unassessed = sections.filter(s => s.status === 'no_assessment');
  if (compact) return (
    <p className="text-xs text-slate-600 py-1.5">
      <span className="font-semibold text-slate-800">{flags.length} {flags.length === 1 ? 'finding' : 'findings'}</span>
      {unverified.length ? `, ${unverified.length} unverified` : ''} placed in the text below, under the paragraphs they are about.
      {state.data.corpus_meta?.notice ? <span className="block text-amber-700 mt-0.5">{state.data.corpus_meta.notice}</span> : null}
      {g.overall_summary ? <span className="block text-slate-500 mt-0.5">{g.overall_summary}</span> : null}
      <span className="block text-[11px] text-slate-400 mt-0.5">{state.data.citations_verified || 0} verified citations{state.data.generated_at ? ` · run ${fmtDate(state.data.generated_at)}` : ''}{runAgain}</span>
    </p>
  );
  return (
    <div className="py-3 space-y-2.5">
      {state.data.corpus_meta?.notice && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">{state.data.corpus_meta.notice}</p>
      )}
      {g.overall_summary && <p className="text-xs text-slate-600 leading-relaxed">{g.overall_summary}</p>}
      {flags.map(s => (
        <div key={s.index} className="rounded-lg border border-slate-200 bg-white p-3">
          <p className="text-xs font-semibold text-slate-700">{s.heading}</p>
          {s.applies === 'conditional' && s.condition && (
            <ConditionAnswer condition={s.condition} stored={condAnswers?.get(s.condition)} compact />
          )}
          <p className="text-xs text-slate-600 mt-1">{s.note}</p>
          {(s.citations || []).map((c, i) => (
            <div key={i} className="mt-1.5 border-l-2 border-slate-300 pl-2.5">
              <p className="text-[11px] font-semibold text-slate-500">{c.source === 'state_contract' ? (c.label || 'State Medicaid contract') : c.source === 'requirement' ? (c.label || 'Requirement on file') : c.citation}</p>
              <blockquote className="text-[11.5px] italic text-slate-500">&ldquo;{c.quote}&rdquo;</blockquote>
            </div>
          ))}
        </div>
      ))}
      <TakeOnBlock items={g.take_on} compact />
      {unverified.map(s => (
        <div key={s.index} className="rounded-lg border border-slate-200 bg-white p-3 opacity-75">
          <p className="text-xs font-semibold text-slate-600">{s.heading}</p>
          <p className="text-xs text-slate-500 mt-1">{s.note}</p>
          <p className="text-[11px] text-slate-400 mt-1">{s.caveat}</p>
        </div>
      ))}
      {unassessed.length > 0 && <p className="text-[11.5px] text-amber-700">Not assessed: {unassessed.map(s => s.heading).join('; ')}.</p>}
      {clean.length > 0 && <p className="text-[11.5px] text-slate-500">No changes found affecting: {clean.map(s => s.heading).join('; ')}.</p>}
      {(g.corpus_gaps || []).map((gap, i) => <p key={i} className="text-[11px] text-slate-400">{gap}</p>)}
      <p className="text-[11px] text-slate-400">
        {state.data.citations_verified || 0} verified citations · {state.data.citations_dropped || 0} removed in verification
        {state.data.generated_at ? ` · run ${fmtDate(state.data.generated_at)}` : ''}{runAgain}
      </p>
    </div>
  );
}

/**
 * Compliance edits a submission before approving: the working document (the
 * owner's edits already applied) with the same change / add / erase controls
 * the owner had. Saved edits are written into the Word file at approval.
 */
function ComplianceEditPanel({ task, onSaved, guide = null }) {
  const [doc, setDoc] = useState(null);
  const [ops, setOps] = useState([]);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [proposals, setProposals] = useState({});      // paragraph index -> { after, heading, citation }
  const [drafted, setDrafted] = useState(() => new Set());
  const [placing, setPlacing] = useState(false);
  const [discuss, setDiscuss] = useState(null);        // { index, selected } — teach the agent at this text
  // Verified findings become suggested wording on the paragraph itself, the
  // same way the owner gets them; every finding also sits as a note under the
  // paragraph it is about. Nothing here changes the policy until Save.
  useEffect(() => {
    if (!guide || doc?.format !== 'docx') return undefined;
    const sections = Array.isArray(guide.guidance?.sections) ? guide.guidance.sections : [];
    let alive = true;
    (async () => {
      // Every CONFIRMED finding gets wording: a verified flag, and a
      // conditional one once the organization has answered yes to its condition.
      const answers = await fetchConditionAnswers();
      const confirmed = (sec) => sec.status === 'flag' && (sec.citations || []).length
        && (sec.applies !== 'conditional' || (sec.condition && answers?.get(sec.condition)?.answer === 'yes'));
      const toPlace = sections.filter((sec) => confirmed(sec) && !drafted.has(sec.heading));
      if (!toPlace.length || !alive) return;
      setPlacing(true);
      for (const sec of toPlace) {
        if (!alive) break;
        setDrafted((prev) => new Set([...prev, sec.heading]));
        try {
          const d = await fetch(`${API}/api/ppm-review/tasks/${task.id}/draft-change-compliance`, {
            method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ heading: sec.heading, note: sec.note, quotes: (sec.citations || []).map(c => c.quote).filter(Boolean).slice(0, 6) }),
          }).then(r => r.json());
          if (alive && d.success && (d.edits || []).length) {
            setProposals((prev) => {
              const next = { ...prev };
              const citation = sec.citations?.[0] || null;
              for (const e of d.edits) if (next[e.index] == null) next[e.index] = { after: e.after, heading: d.heading || sec.heading, citation };
              return next;
            });
          }
        } catch { /* a failed draft places nothing; the note still shows */ }
      }
      if (alive) setPlacing(false);
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guide, doc, task.id]);
  const comments = (guide && doc?.format === 'docx')
    ? anchorFindings(guide.guidance?.sections, doc.paragraphs, { skipIndexes: new Set(Object.keys(proposals).map(Number)), skipHeadings: new Set(Object.values(proposals).map(p => String(p.heading || '').toLowerCase().trim())) })
    : null;
  useEffect(() => {
    let alive = true;
    fetch(`${API}/api/ppm-review/tasks/${task.id}/working-document`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => {
        if (!alive) return;
        setDoc(d.success ? d : { format: 'error', message: d.message });
        if (d.success && Array.isArray(d.compliance_edits)) setOps(pairsToOps(d.compliance_edits));
      })
      .catch(() => { if (alive) setDoc({ format: 'error', message: 'Could not load the document.' }); });
    return () => { alive = false; };
  }, [task.id]);

  const save = async () => {
    setBusy(true); setNotice('');
    try {
      const d = await fetch(`${API}/api/ppm-review/tasks/${task.id}/compliance-edit`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ edits: toRequestEdits(ops) }),
      }).then(r => r.json());
      if (d.success) { setNotice(d.changes ? `${d.changes} change${d.changes === 1 ? '' : 's'} saved. They are written into the document when you approve.` : 'Your edits were cleared.'); onSaved && onSaved(); }
      else setNotice(d.message || 'That did not go through.');
    } catch { setNotice('That did not go through.'); }
    setBusy(false);
  };

  if (!doc) return <p className="text-xs text-slate-400 py-2">Loading the document…</p>;
  if (doc.format === 'error') return <p className="text-xs text-amber-700 py-2">{doc.message}</p>;
  if (doc.format === 'none') return <p className="text-xs text-amber-700 py-2">No document on record for this policy; nothing to edit.</p>;
  if (doc.format === 'other') return (
    <p className="text-xs text-slate-600 py-2">
      {doc.file_name} is a PDF or another non-Word file: paragraph editing and the assistant are not available for it. Open the file, review it, then approve or return it to the owner with your notes.
      {task?.id && <> <a href={`${API}/api/ppm-review/tasks/${task.id}/file`} className="font-semibold text-orange-700 no-underline">Open the document</a></>}
    </p>
  );
  const n = editsSummary(toRequestEdits(ops));
  const count = toRequestEdits(ops).length;
  return (
    <div className="py-3 space-y-2.5">
      <p className="text-xs text-slate-500">This is the policy as the owner submitted it{task.mode === 'edited' ? ', with their edits applied' : ''}. Click a paragraph to change it; hover for Add below and Erase. Save, then approve.</p>
      {Array.isArray(doc.suggestions) && doc.suggestions.length > 0 && (
        <div className="rounded-xl bg-white ring-1 ring-slate-200 p-3.5 space-y-2.5">
          <p className="text-[11px] font-semibold uppercase tracking-[0.05em] text-slate-500">Suggested changes and what the owner did with them</p>
          {doc.suggestions.map((sg, i) => (
            <div key={i} className="border-l-2 border-slate-200 pl-3 space-y-1">
              <p className="text-[12px] text-slate-500">{sg.heading || 'Suggested change'} ·{' '}
                <span className={`font-semibold ${sg.action === 'dismissed' ? 'text-slate-600' : 'text-emerald-700'}`}>
                  {sg.action === 'accepted' ? 'Owner used the suggested wording' : sg.action === 'edited' ? 'Owner used it with their own edits' : 'Owner dismissed it'}
                </span>
              </p>
              {sg.before ? (
                <p className="text-[12px] text-slate-600 leading-snug"><DiffText before={sg.before} after={sg.after || sg.before} mode="before" /></p>
              ) : null}
              {sg.after && sg.action !== 'dismissed' ? (
                <p className="text-[12px] text-slate-700 leading-snug"><DiffText before={sg.before || ''} after={sg.after} mode="after" /></p>
              ) : null}
            </div>
          ))}
        </div>
      )}
      {placing && <p className="text-[12px] font-medium text-sky-800">Placing the suggested wording into the policy…</p>}
      <PolicyParagraphEditor paragraphs={doc.paragraphs} blocks={doc.blocks} docLook headerLogo={doc.header_logo} value={ops} onChange={setOps} maxHeight="max-h-none"
        proposals={proposals} comments={comments}
        onResolveProposal={(idx, action, prop, beforeText) => {
          // Recorded on the task and in the agent's ledger, exactly as the owner's actions are.
          if (action) {
            fetch(`${API}/api/ppm-review/tasks/${task.id}/proposal-disposition`, {
              method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ index: idx, heading: prop?.heading || '', action, before: beforeText || '', after: prop?.after || '' }),
            }).catch(() => {});
          }
          setProposals((prev) => { const next = { ...prev }; delete next[idx]; return next; });
        }}
        onDiscuss={(idx, sel) => setDiscuss((d) => (d?.index === idx ? null : { index: idx, selected: sel }))}
        discussIndex={discuss?.index}
        renderDiscuss={() => <DiscussPanel task={task} index={discuss.index} selected={discuss.selected} viewer="compliance" onClose={() => setDiscuss(null)} />} />
      <div className="flex items-center gap-3 flex-wrap">
        <button type="button" disabled={busy} onClick={save}
          className="px-3 py-1.5 rounded-lg bg-slate-800 text-white text-xs font-semibold disabled:opacity-50">
          {busy ? 'Saving…' : count ? `Save ${count} change${count === 1 ? '' : 's'}` : 'Save (clears your edits)'}
        </button>
        {count > 0 && <span className="text-[11.5px] text-slate-500">{[n.replaced && `${n.replaced} edited`, n.inserted && `${n.inserted} added`, n.deleted && `${n.deleted} erased`].filter(Boolean).join(', ')}</span>}
        {notice && <span className="text-[11.5px] font-medium text-emerald-700">{notice}</span>}
      </div>
    </div>
  );
}

function PublishNotes({ notes, version }) {
  if (!notes) return <span className="text-xs text-slate-400">v cut, clock reset</span>;
  const parts = [];
  if (version) parts.push(`v${version} cut`);
  if (notes.file === 'docx') {
    parts.push(notes.revision_row ? 'revision row added' : 'no revision row');
    parts.push(Array.isArray(notes.header_stamped) && notes.header_stamped.includes('revision') ? 'revision date at the top updated' : 'no revision-date label found at the top');
  } else parts.push(notes.file === 'none' ? 'no document on record' : 'not a Word document, register only');
  const ce = notes.compliance_edits;
  if (ce && (ce.replaced || ce.inserted || ce.deleted)) parts.push(`compliance edits: ${[ce.replaced && `${ce.replaced} edited`, ce.inserted && `${ce.inserted} added`, ce.deleted && `${ce.deleted} erased`].filter(Boolean).join(', ')}`);
  parts.push('clock reset');
  return <span className="text-xs text-slate-500">{parts.join(' · ')}</span>;
}

export default function AnnualReviewView() {
  const [campaigns, setCampaigns] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [dash, setDash] = useState(null);
  const [approveDates, setApproveDates] = useState({});
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [notice, setNotice] = useState('');
  const [defaults, setDefaults] = useState(null);         // { requirement_basis, policies }
  const [guidanceOpen, setGuidanceOpen] = useState(null); // task id with guidance expanded
  const [diffOpen, setDiffOpen] = useState(null);         // task id with the in-app edit diff expanded
  const [editOpen, setEditOpen] = useState(null);         // task id with the compliance editor open
  const [guideFor, setGuideFor] = useState({});           // task id -> ready guidance, for the editor's inline notes
  const [selected, setSelected] = useState(() => new Set()); // submitted task ids picked for one approval
  const [bulk, setBulk] = useState(null);                  // { confirm } | { running, done, total } | { notice }
  const [tab, setTab] = useState('progress');             // 'progress' | 'correspondence'

  const loadCampaigns = useCallback(() => {
    fetch(`${API}/api/ppm-review/campaigns`, { credentials: 'include' })
      .then(r => r.json()).then(d => { if (d.success) setCampaigns(d.campaigns || []); }).catch(() => {});
  }, []);
  const loadDash = useCallback((id) => {
    fetch(`${API}/api/ppm-review/campaigns/${id}`, { credentials: 'include' })
      .then(r => r.json()).then(d => { if (d.success) setDash(d); }).catch(() => {});
  }, []);
  useEffect(() => { loadCampaigns(); }, [loadCampaigns]);
  useEffect(() => { if (openId) { loadDash(openId); setTab('progress'); } else setDash(null); }, [openId, loadDash]);
  useEffect(() => {
    if (!showCreate || defaults) return;
    fetch(`${API}/api/ppm-review/launch-defaults`, { credentials: 'include' })
      .then(r => r.json()).then(d => { if (d.success) setDefaults(d); }).catch(() => {});
  }, [showCreate, defaults]);

  const launch = async (e) => {
    e.preventDefault();
    const form = e.target;
    setCreating(true); setNotice('');
    try {
      const d = await fetch(`${API}/api/ppm-review/campaigns`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.value, purpose: form.purpose.value || null, due_date: form.due_date.value,
          requirement_basis: form.requirement_basis.value || null,
          requirement_policy_id: form.requirement_policy_id.value || null,
        }),
      }).then(r => r.json());
      if (d.success) {
        const skipped = d.skipped_departments || [];
        setNotice(`Launched: ${d.tasks} policies assigned. Emails to ${d.owners_to_email} owner(s) are going out now${d.unassigned ? `; ${d.unassigned} policies have no owner yet` : ''}${skipped.length ? `; ${skipped.length} department${skipped.length === 1 ? '' : 's'} already reviewed this year — their documents are being sent to you` : ''}.`);
        setShowCreate(false); loadCampaigns(); setOpenId(d.campaign.id);
      } else setNotice(d.message || 'The launch failed.');
    } catch { setNotice('The launch failed.'); }
    setCreating(false);
  };

  // The date each submitted task needs before approval, exactly as the single
  // Approve button asks for it: the board date when compliance owns the
  // policy, the owner's review date when none is on record.
  const approvePayload = (t) => {
    const picked = approveDates[t.id] || '';
    if (t.compliance_owned) return picked ? { board_approved_on: picked } : null;
    if (!t.reviewed_on) return picked ? { review_date: picked } : null;
    return {};
  };
  const toggleSelected = (id) => setSelected(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const approveSelected = async () => {
    const tasks = (dash?.tasks || []).filter(t => selected.has(t.id) && t.status === 'submitted');
    const ready = tasks.filter(t => approvePayload(t));
    const missing = tasks.filter(t => !approvePayload(t));
    if (!ready.length) { setBulk({ notice: 'Each selected policy needs its date first (board approval date, or the date of the owner\'s review).' }); return; }
    setBulk({ running: true, done: 0, total: ready.length });
    let ok = 0; const failed = [];
    for (const t of ready) {
      const d = await fetch(`${API}/api/ppm-review/tasks/${t.id}/approve`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(approvePayload(t)),
      }).then(r => r.json()).catch(() => ({}));
      if (d.success) ok += 1; else failed.push(`${t.policy_number ? `${t.policy_number}: ` : ''}${t.title}${d.message ? ` (${d.message})` : ''}`);
      setBulk({ running: true, done: ok + failed.length, total: ready.length });
    }
    setSelected(new Set());
    setBulk({ notice: [`${ok} approved.`, failed.length ? `Not approved: ${failed.join('; ')}.` : '', missing.length ? `${missing.length} skipped for a missing date: ${missing.map(t => t.policy_number || t.title).join(', ')}.` : ''].filter(Boolean).join(' ') });
    if (openId) loadDash(openId);
  };

  const decide = async (taskId, action, extra = {}) => {
    const note = action === 'return' ? window.prompt('Tell the owner what to revisit. The edits you saved on the text go to them as suggested changes:') : null;
    if (action === 'return' && !note) return;
    const d = await fetch(`${API}/api/ppm-review/tasks/${taskId}/${action}`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...(note ? { note } : {}), ...extra }),
    }).then(r => r.json()).catch(() => ({}));
    if (d.success && openId) { setEditOpen(null); loadDash(openId); }
    if (!d.success) window.alert(d.message || 'That did not go through.');
  };

  // ── Campaign list ─────────────────────────────────────────────────────────
  if (!openId) {
    return (
      <div className="px-8 py-6 max-w-5xl">
        <div className="flex items-start justify-between gap-4 mb-1">
          <div>
            <h2 className="text-lg font-bold text-slate-800">Annual Review</h2>
            <p className="text-sm text-slate-500 mt-0.5">Launch the review, and every policy owner is emailed their list. You approve each submission before anything publishes.</p>
          </div>
          <button type="button" onClick={() => setShowCreate(v => !v)}
            className="px-4 py-2 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 text-white text-sm font-medium shadow-sm hover:opacity-90 transition">
            Begin annual review
          </button>
        </div>
        {notice && <p className="text-sm text-emerald-700 font-medium mt-2">{notice}</p>}
        <RuleHomesPanel />

        {showCreate && (
          <form onSubmit={launch} className="mt-4 p-4 rounded-xl border border-slate-200 bg-slate-50/60 space-y-3 max-w-2xl">
            <div className="flex flex-wrap gap-3">
              <div className="flex-1 min-w-[220px]">
                <label className="block text-xs font-medium text-slate-600 mb-1">Name</label>
                <input name="name" required defaultValue={`Annual Policy Review ${new Date().getFullYear()}`}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Due date</label>
                <input name="due_date" type="date" required className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Why this is required (goes in the email to every owner)</label>
              <textarea name="requirement_basis" rows={3} key={defaults ? 'loaded' : 'blank'}
                defaultValue={defaults?.requirement_basis || ''}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Our policy that sets the review cadence (cited in the email)</label>
              <select name="requirement_policy_id" defaultValue="" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white">
                <option value="">None — cite the regulation only</option>
                {(defaults?.policies || []).map(p => <option key={p.id} value={p.id}>{p.policy_number ? `${p.policy_number}: ` : ''}{p.title}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Anything else for the owners (optional)</label>
              <textarea name="purpose" rows={2}
                placeholder="A sentence from you, added under the requirement."
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            </div>
            <p className="text-xs text-slate-500">Covers every active policy. Each owner gets one email with their policies grouped by department, what to do, and the due date. Departments whose policies were all reviewed earlier this year are not asked again; their current documents are zipped and emailed to you. Reminders are sent from Workstream.</p>
            <button type="submit" disabled={creating}
              className="px-4 py-2 rounded-xl bg-slate-800 text-white text-sm font-medium disabled:opacity-50">
              {creating ? 'Launching and emailing owners…' : 'Launch and email every owner'}
            </button>
          </form>
        )}

        <div className="mt-5">
          {campaigns.length === 0 ? (
            <p className="text-sm text-slate-500 py-8">No reviews yet. Begin one and every policy owner gets their assignment by email.</p>
          ) : (
            <div className="divide-y divide-slate-100 border border-slate-200 rounded-xl overflow-hidden bg-white">
              {campaigns.map(c => (
                <button key={c.id} type="button" onClick={() => setOpenId(c.id)}
                  className="w-full flex items-center justify-between gap-4 px-4 py-3.5 text-left hover:bg-slate-50 transition">
                  <div>
                    <p className="text-sm font-semibold text-slate-800">{c.name}</p>
                    <p className="text-xs text-slate-500 mt-0.5">Due {fmtDate(c.due_date)} · {c.status}</p>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    {c.submitted > 0 && <span className="px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-200 font-medium">{c.submitted} to review</span>}
                    <span className="text-slate-400">{c.approved}/{c.tasks} approved</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── One campaign's dashboard ──────────────────────────────────────────────
  const skipped = Array.isArray(dash?.campaign?.skipped_departments) ? dash.campaign.skipped_departments : [];
  return (
    <div className="px-8 py-6 max-w-6xl">
      <div className="flex items-center justify-between mb-3">
        <button type="button" onClick={() => { setOpenId(null); loadCampaigns(); }}
          className="text-sm text-slate-500 hover:text-slate-700">← All reviews</button>
        {dash?.campaign?.status === 'active' && (
          <span className="flex items-center gap-2">
            <button type="button"
              onClick={async () => {
                const d = await fetch(`${API}/api/ppm-review/campaigns/${openId}/sweep`, { method: 'POST', credentials: 'include' }).then(r => r.json()).catch(() => ({}));
                if (!d.success) { window.alert(d.message || 'Could not add the policies.'); return; }
                if (!d.added) { window.alert('Every active policy is already part of this review.'); return; }
                window.alert(`${d.added} ${d.added === 1 ? 'policy' : 'policies'} added to this review.${d.owners_to_email ? ' The owners are being emailed now.' : ''}${d.unassigned ? ` ${d.unassigned} ${d.unassigned === 1 ? 'policy has' : 'policies have'} no owner yet; assign one in the library and press this again.` : ''}`);
                loadDash(openId);
              }}
              className="px-3 py-1.5 rounded-lg border border-orange-300 text-xs font-semibold text-orange-700 hover:border-orange-400"
              title="Policies made active after this review was issued get their review task now, assigned to their owner with the same email.">
              Add newly active policies
            </button>
            <button type="button"
              onClick={async () => {
                if (!window.confirm('Close this review? Open tasks disappear from owner home pages; approvals and records stay.')) return;
                const d = await fetch(`${API}/api/ppm-review/campaigns/${openId}/close`, { method: 'POST', credentials: 'include' }).then(r => r.json()).catch(() => ({}));
                if (d.success) { setOpenId(null); loadCampaigns(); }
                else window.alert(d.message || 'Could not close the campaign.');
              }}
              className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs text-slate-600 hover:border-slate-300">
              Close review
            </button>
          </span>
        )}
      </div>
      {!dash ? <p className="text-sm text-slate-500 py-8">Loading…</p> : (
        <>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-bold text-slate-800">{dash.campaign.name}</h2>
              <p className="text-sm text-slate-500 mt-0.5">
                Due {fmtDate(dash.campaign.due_date)}
                {(() => {
                  const withOwner = (dash.tasks || []).filter(t => t.owner_name);
                  const sent = withOwner.filter(t => t.launch_email_sent_at).length;
                  if (!withOwner.length) return null;
                  return sent < withOwner.length
                    ? ` · Emails going out: ${sent} of ${withOwner.length} owners notified — refresh to update`
                    : ` · Every owner notified by email`;
                })()}
                {dash.campaign.launch_summary_sent_at ? ' · Launch summary sent to you' : ''}
              </p>
              {dash.campaign.requirement_policy_title && (
                <p className="text-xs text-slate-400 mt-0.5">Cadence policy cited: {dash.campaign.requirement_policy_number ? `${dash.campaign.requirement_policy_number}: ` : ''}{dash.campaign.requirement_policy_title}</p>
              )}
            </div>
            <div className="flex gap-5 text-center">
              <div><p className="text-xl font-bold text-slate-800">{dash.counts.pending || 0}</p><p className="text-[11px] text-slate-500">Waiting on owners</p></div>
              <div><p className={`text-xl font-bold ${dash.counts.submitted ? 'text-sky-600' : 'text-slate-800'}`}>{dash.counts.submitted || 0}</p><p className="text-[11px] text-slate-500">Ready for you</p></div>
              <div><p className="text-xl font-bold text-emerald-600">{dash.counts.approved || 0}</p><p className="text-[11px] text-slate-500">Approved</p></div>
              {dash.counts.unassigned > 0 && <div><p className="text-xl font-bold text-amber-600">{dash.counts.unassigned}</p><p className="text-[11px] text-slate-500">No owner</p></div>}
            </div>
          </div>

          {skipped.length > 0 && (
            <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3">
              <p className="text-xs font-semibold text-slate-600">Already reviewed this year — not asked again; documents sent to you</p>
              <ul className="mt-1 text-xs text-slate-600 space-y-0.5">
                {skipped.map(d => (
                  <li key={d.department_id} className="flex flex-wrap items-center gap-2">
                    <span>{d.name} · {d.policy_count} {d.policy_count === 1 ? 'policy' : 'policies'}</span>
                    <span className="text-slate-400">{d.mail_id ? (d.attached ? 'emailed with the zip attached' : 'emailed; the zip was too large to attach') : d.error ? `not sent: ${d.error}` : 'sending…'}</span>
                    <a href={`${API}/api/ppm-review/campaigns/${openId}/archive/${d.department_id}`} className="text-orange-700 hover:text-orange-800 font-medium">Download the zip</a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-4 inline-flex rounded-xl border border-slate-200 bg-white p-1 gap-1">
            {[['progress', 'Progress'], ['questions', 'Questions'], ['correspondence', 'Correspondence']].map(([id, label]) => (
              <button key={id} type="button" onClick={() => setTab(id)}
                className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition-colors ${tab === id ? 'bg-orange-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'}`}>
                {label}
              </button>
            ))}
          </div>

          {tab === 'questions' ? (
            <CampaignQuestions campaignId={openId} />
          ) : tab === 'correspondence' ? (
            <div className="mt-4">
              <p className="text-xs text-slate-500 mb-3">Every email this review sent and every reply it received, by department. Download any message as the real email (.eml).</p>
              <ReviewCorrespondence scope="campaign" campaignId={openId} />
            </div>
          ) : (
          <div className="mt-4 border border-slate-200 rounded-xl overflow-hidden bg-white">
            {(selected.size > 0 || bulk) && (
              <div className="flex items-center gap-3 flex-wrap px-4 py-2.5 bg-emerald-50 border-b border-emerald-200 text-xs">
                {bulk?.running ? (
                  <span className="font-semibold text-emerald-800">Approving {bulk.done} of {bulk.total}…</span>
                ) : bulk?.notice ? (
                  <>
                    <span className="text-slate-700">{bulk.notice}</span>
                    <button type="button" onClick={() => setBulk(null)} className="text-slate-500 hover:text-slate-700 font-semibold">Dismiss</button>
                  </>
                ) : bulk?.confirm ? (
                  <>
                    <span className="text-slate-700">Approve {selected.size} selected {selected.size === 1 ? 'policy' : 'policies'} now? Each is approved exactly as the single Approve button would, with its date.</span>
                    <button type="button" onClick={approveSelected} className="px-3 py-1 rounded-lg bg-emerald-600 text-white font-semibold hover:bg-emerald-700">Yes, approve {selected.size}</button>
                    <button type="button" onClick={() => setBulk(null)} className="px-3 py-1 rounded-lg border border-slate-300 text-slate-600 font-semibold">Not yet</button>
                  </>
                ) : (
                  <>
                    <span className="font-semibold text-emerald-800">{selected.size} selected</span>
                    <button type="button" onClick={() => setBulk({ confirm: true })} className="px-3 py-1 rounded-lg bg-emerald-600 text-white font-semibold hover:bg-emerald-700">Approve selected</button>
                    <button type="button" onClick={() => setSelected(new Set())} className="text-slate-500 hover:text-slate-700 font-semibold">Clear</button>
                    <span className="text-slate-500">Policies missing their date are skipped and named.</span>
                  </>
                )}
              </div>
            )}
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-500 border-b border-slate-200 bg-slate-50/60">
                  <th className="pl-4 pr-1 py-2.5 w-8">
                    {(() => { const ids = (dash.tasks || []).filter(t => t.status === 'submitted').map(t => t.id); const all = ids.length > 0 && ids.every(id => selected.has(id));
                      return ids.length > 0 ? <input type="checkbox" aria-label="Select every policy ready for your review" checked={all} onChange={() => setSelected(all ? new Set() : new Set(ids))} className="accent-emerald-600" /> : null; })()}
                  </th>
                  <th className="px-4 py-2.5 font-semibold">Policy</th>
                  <th className="px-4 py-2.5 font-semibold">Owner</th>
                  <th className="px-4 py-2.5 font-semibold">Department</th>
                  <th className="px-4 py-2.5 font-semibold">Status</th>
                  <th className="px-4 py-2.5 font-semibold">Attested by</th>
                  <th className="px-4 py-2.5 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(dash.tasks || []).map(t => {
                  const showGuidance = (t.guidance_summary && ['submitted', 'approved', 'returned'].includes(t.status)) || t.status === 'submitted';
                  const ce = t.compliance_edits && Array.isArray(t.compliance_edits) ? t.compliance_edits.length : 0;
                  return (
                  <React.Fragment key={t.id}>
                  <tr className={selected.has(t.id) ? 'bg-emerald-50/40' : ''}>
                    <td className="pl-4 pr-1 py-3 w-8 align-top">
                      {t.status === 'submitted' && <input type="checkbox" aria-label={`Select ${t.title}`} checked={selected.has(t.id)} onChange={() => toggleSelected(t.id)} className="accent-emerald-600" />}
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-800">{t.policy_number ? `${t.policy_number}: ` : ''}{t.title}</p>
                      {(t.mode === 'replaced' || t.mode === 'edited') && t.ai_change_summary && <p className="text-xs text-slate-700 mt-0.5"><span className="font-semibold">What changed:</span> {t.ai_change_summary}</p>}
                      {(t.mode === 'replaced' || t.mode === 'edited') && t.change_summary && <p className="text-xs text-slate-500 mt-0.5"><span className="font-semibold">Owner's note:</span> {t.change_summary}</p>}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{t.owner_name || <span className="text-amber-600 font-medium">Unassigned</span>}{t.delegate_name ? <span className="text-xs text-slate-400 block">handed to {t.delegate_name}</span> : null}{t.owner_unreachable ? <span className="inline-block mt-1 px-2 py-0.5 rounded-md bg-amber-50 text-amber-700 border border-amber-200 text-[11px] font-medium">Owner unreachable — never emailed</span> : null}</td>
                    <td className="px-4 py-3 text-slate-600">{t.department_name || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${STATUS_CHIP[t.status] || STATUS_CHIP.pending}`}>{STATUS_LABEL[t.status] || t.status}</span>
                      {t.mode && <span className="text-[11px] text-slate-400 block mt-0.5">{t.mode === 'replaced' ? 'Revised document' : t.mode === 'edited' ? 'Edited in the app' : 'Confirmed as current'}</span>}
                      {ce > 0 && <span className="text-[11px] text-orange-700 block mt-0.5">{ce} compliance edit{ce === 1 ? '' : 's'} saved{t.compliance_edited_by_name ? ` by ${t.compliance_edited_by_name}` : ''}</span>}
                      {t.mode === 'edited' && Array.isArray(t.section_edits) && t.section_edits.length > 0 && (
                        <button type="button" onClick={() => setDiffOpen(diffOpen === t.id ? null : t.id)}
                          className="text-[11px] text-orange-700 hover:text-orange-800 font-medium block mt-0.5">
                          {diffOpen === t.id ? 'Hide changes' : `${t.section_edits.length} change${t.section_edits.length === 1 ? '' : 's'} — view`}
                        </button>
                      )}
                      {diffOpen === t.id && Array.isArray(t.section_edits) && (
                        <div className="mt-2 space-y-2 max-w-md">
                          {t.section_edits.map((e, i) => (
                            <div key={i} className="rounded-lg border border-slate-200 bg-white p-2">
                              {e.op === 'insert_after' ? (
                                <p className="text-[11px] text-emerald-700 leading-snug"><span className="font-semibold uppercase text-[10px] mr-1">added</span>{e.after}</p>
                              ) : e.op === 'delete' ? (
                                <p className="text-[11px] text-red-700 line-through leading-snug"><span className="font-semibold uppercase text-[10px] mr-1 no-underline">erased</span>{e.before}</p>
                              ) : (
                                <>
                                  <p className="text-[11px] text-slate-600 leading-snug"><DiffText before={e.before} after={e.after} mode="before" /></p>
                                  <p className="text-[11px] text-slate-700 leading-snug mt-1"><DiffText before={e.before} after={e.after} mode="after" /></p>
                                </>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-600 text-xs">{t.attestation_name ? `${t.attestation_name}${t.attested_at ? `, ${fmtDate(t.attested_at)}` : ''}` : '—'}</td>
                    <td className="px-4 py-3">
                      {t.status === 'submitted' ? (
                        <div className="flex gap-1.5 flex-wrap">
                          {t.replacement_file_name && (
                            <a href={`${API}/api/ppm-review/tasks/${t.id}/file`} className="px-2.5 py-1 rounded-lg border border-slate-200 text-xs text-slate-600 hover:border-slate-300">Document</a>
                          )}
                          {showGuidance && (
                            <button type="button" onClick={() => setGuidanceOpen(guidanceOpen === t.id ? null : t.id)}
                              className={`px-2.5 py-1 rounded-lg border text-xs ${guidanceOpen === t.id ? 'border-slate-400 text-slate-800 bg-slate-50' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}>
                              {t.guidance_summary
                                ? `Review${t.guidance_summary.status === 'ready' ? ` · ${t.guidance_summary.sections_flagged || 0} ${(t.guidance_summary.sections_flagged || 0) === 1 ? 'flag' : 'flags'}` : t.guidance_summary.status === 'generating' ? ' · running' : ''}`
                                : 'Run the review'}
                            </button>
                          )}
                          <button type="button" onClick={() => setEditOpen(editOpen === t.id ? null : t.id)}
                            className={`px-2.5 py-1 rounded-lg border text-xs ${editOpen === t.id ? 'border-orange-400 text-orange-800 bg-orange-50' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}>
                            {editOpen === t.id ? 'Close editor' : 'Edit before approving'}
                          </button>
                          {(t.compliance_owned || !t.reviewed_on) && (
                            <input type="date" value={approveDates[t.id] || ''}
                              onChange={e => setApproveDates(x => ({ ...x, [t.id]: e.target.value }))}
                              title={t.compliance_owned ? 'Board approval date' : "Date of the owner's review"}
                              className="px-2 py-1 rounded-lg border border-amber-300 text-xs text-slate-700" />
                          )}
                          <button type="button" onClick={() => {
                            const picked = approveDates[t.id] || '';
                            if (t.compliance_owned && !picked) { window.alert('Enter the board approval date — the date the board approved this policy.'); return; }
                            if (!t.compliance_owned && !t.reviewed_on && !picked) { window.alert("Select the date of the owner's review."); return; }
                            decide(t.id, 'approve', t.compliance_owned ? { board_approved_on: picked } : (t.reviewed_on ? {} : { review_date: picked }));
                          }}
                            className="px-2.5 py-1 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700">Approve</button>
                          <button type="button" onClick={() => decide(t.id, 'return')}
                            className="px-2.5 py-1 rounded-lg border border-slate-200 text-xs text-slate-600 hover:border-slate-300">Return</button>
                        </div>
                      ) : t.status === 'approved' ? (
                        <div className="flex gap-1.5 flex-wrap items-center">
                          <PublishNotes notes={t.publish_notes} version={null} />
                          {showGuidance && (
                            <button type="button" onClick={() => setGuidanceOpen(guidanceOpen === t.id ? null : t.id)}
                              className="px-2.5 py-1 rounded-lg border border-slate-200 text-xs text-slate-600 hover:border-slate-300">
                              Guidance{t.guidance_summary.status === 'ready' ? ` · ${t.guidance_summary.sections_flagged || 0} ${(t.guidance_summary.sections_flagged || 0) === 1 ? 'flag' : 'flags'}` : ''}
                            </button>
                          )}
                        </div>
                      ) : <span className="text-xs text-slate-400">—</span>}
                    </td>
                  </tr>
                  {guidanceOpen === t.id && (
                    <tr>
                      <td colSpan={7} className="px-4 bg-slate-50/60 border-t border-slate-100">
                        <GuidanceDetail taskId={t.id} canRun={t.status === 'submitted'} onChanged={() => loadDash(openId)} />
                      </td>
                    </tr>
                  )}
                  {editOpen === t.id && t.status === 'submitted' && (
                    <tr>
                      <td colSpan={7} className="p-0 border-t border-orange-100">
                        <div className="fixed inset-0 z-50 bg-slate-900/50 overflow-y-auto p-3 sm:p-8"
                          onClick={(e) => { if (e.target === e.currentTarget) setEditOpen(null); }}>
                          <div className="mx-auto w-[min(1000px,100%)] rounded-2xl bg-slate-100 shadow-2xl p-4 sm:p-6 space-y-3">
                            <div className="flex items-center justify-between gap-3">
                              <p className="text-[15px] font-bold text-slate-900">{t.policy_number ? `${t.policy_number}: ` : ''}{t.title}</p>
                              <button type="button" onClick={() => setEditOpen(null)}
                                className="px-3.5 py-1.5 rounded-lg bg-white ring-1 ring-slate-200 text-[12px] font-semibold text-slate-600 hover:ring-slate-300">
                                Close
                              </button>
                            </div>
                            <div className="rounded-xl bg-white ring-1 ring-slate-200 px-4 py-2">
                              <p className="text-[11px] font-semibold uppercase tracking-[0.05em] text-slate-500 pt-1">The review against the final rule, the memos and the state contract</p>
                              <GuidanceDetail taskId={t.id} canRun={t.status === 'submitted'} compact onData={(d) => setGuideFor((prev) => (prev[t.id] === d ? prev : { ...prev, [t.id]: d }))} />
                            </div>
                            <AskGuidePanel taskId={t.id} endpoint="ask-guide" title="Ask the agent about this policy" />
                            <ComplianceEditPanel task={t} guide={guideFor[t.id] || null} onSaved={() => loadDash(openId)} />
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Owner questions asked from the policy text, open ones first. Compliance
 * answers here; the answer lands back on the policy and by email, and the
 * wording decision stays the owner's.
 */
function CampaignQuestions({ campaignId }) {
  const [items, setItems] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    fetch(`${API}/api/ppm-review/campaigns/${campaignId}/questions`, { credentials: 'include' })
      .then(r => r.json()).then(d => setItems(d.success ? (d.questions || []) : []))
      .catch(() => setItems([]));
  }, [campaignId]);
  useEffect(() => { load(); }, [load]);

  const answer = async (id) => {
    const a = (drafts[id] || '').trim();
    if (a.length < 2) { window.alert('Write the answer first.'); return; }
    setBusy(id);
    const d = await fetch(`${API}/api/ppm-review/compliance-questions/${id}/answer`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ answer: a }),
    }).then(r => r.json()).catch(() => ({}));
    setBusy(null);
    if (d.success) { setDrafts(x => ({ ...x, [id]: '' })); load(); }
    else window.alert(d.message || 'That did not go through.');
  };

  if (items === null) return <p className="mt-4 text-sm text-slate-500">Loading…</p>;
  if (!items.length) return <p className="mt-4 text-sm text-slate-500">No questions from owners yet. When someone asks compliance from their policy text, it lands here.</p>;
  return (
    <div className="mt-4 space-y-3">
      <p className="text-xs text-slate-500">Questions owners asked from their policy text, newest and open first. Your answer goes back onto the policy and by email; the wording decision stays with the owner.</p>
      {items.map(q => (
        <div key={q.id} className={`rounded-xl bg-white ring-1 p-4 ${q.status === 'open' ? 'ring-amber-300' : 'ring-slate-200'}`}>
          <p className="text-xs text-slate-400">{q.policy_number ? `${q.policy_number}: ` : ''}{q.title} · asked by {q.asked_by_name || 'an owner'} · {fmtDate(q.created_at)}</p>
          {q.selected_text && <p className="text-[12px] text-slate-500 italic mt-1">About: &ldquo;{q.selected_text.slice(0, 240)}&rdquo;</p>}
          <p className="text-sm font-semibold text-slate-800 mt-1.5">{q.question}</p>
          {q.status === 'answered' ? (
            <p className="text-sm text-slate-700 mt-2">{q.answer}<span className="text-xs text-slate-400"> — {q.answered_by_name || 'compliance'}, {fmtDate(q.answered_at)}</span></p>
          ) : (
            <div className="flex gap-2 items-start mt-2.5">
              <textarea value={drafts[q.id] || ''} onChange={e => setDrafts(x => ({ ...x, [q.id]: e.target.value }))} rows={2} maxLength={4000}
                placeholder="Write the answer…"
                className="flex-1 rounded-xl ring-1 ring-slate-200 px-3.5 py-2 text-sm outline-none focus:ring-2 focus:ring-orange-300" />
              <button type="button" disabled={busy === q.id} onClick={() => answer(q.id)}
                className="px-4 py-2 rounded-xl bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold disabled:opacity-50">
                {busy === q.id ? 'Sending…' : 'Send the answer'}
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

